import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { LoadingPageService } from '@umss/shared/ui';

export const enum FacilityTabDetailEntity {
  Contractor = 'Contractor',
  Admin = 'Admin',
  Invoice = 'Invoice',
  Shift = 'Shift',
  Notification = 'Notification'
}

@Injectable({
  providedIn: 'root'
})
export class FacilityTabTitleService {
  private readonly appName = 'HURO';

  private readonly title = inject(Title);
  private readonly loadingPageService = inject(LoadingPageService);
  constructor() {
    // Listen to loading state changes to update the title accordingly
    this.loadingPageService.navigationEnd$.subscribe((url) => {
      this.setForUrl(url);
    });
    this.setForUrl(window.location.pathname);
  }

  setForUrl(rawUrl: string): void {
    const url = this.normalizeUrl(rawUrl);
    this.setDocumentTitle(this.resolveFallbackTitle(url));
  }

  setDetailTitle(
    entity: FacilityTabDetailEntity | `${FacilityTabDetailEntity}`,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload?: any
  ): void {
    switch (entity) {
      case 'Contractor':
        return this.setDocumentTitle(
          `Professional · ${payload?.user?.fullName || 'Details'}`
        );
      case 'Admin':
        return this.setDocumentTitle(
          `Admin · ${payload?.firstName || ''} ${payload?.lastName || ''}`.trim() ||
            'Details'
        );
      case 'Invoice':
        return this.setDocumentTitle(
          `Facility · ${payload?.invoiceNumber || 'Details'}`
        );
      case 'Shift':
        return this.setDocumentTitle(
          `Shift · ${payload?.scopeOfPracticeDisplay || 'Details'}`
        );
      case 'Notification':
        return this.setDocumentTitle(
          `Notification · ${payload?.title || 'Details'}`
        );
      default:
        return this.setDocumentTitle(this.appName);
    }
  }

  private normalizeUrl(rawUrl: string): string {
    return rawUrl.split('?')[0].toLowerCase();
  }

  private resolveFallbackTitle(url: string): string {
    if (url.includes('/dashboard')) return 'HURO Dashboard';
    if (url.includes('/notifications')) return 'Notifications';
    if (url.includes('/scheduling')) return 'Shifts';
    if (url.includes('/professionals/professionals')) return 'Professionals';
    if (url.includes('/professionals')) return 'Professionals';
    if (url.includes('/payments/invoices')) return 'Invoices';
    if (url.includes('/payments/timesheets')) return 'Timesheets';
    if (url.includes('/payments')) return 'Payments';
    if (url.includes('/billable-rates')) return 'Billable Rates';
    if (url.includes('/admin/roles')) return 'Roles';
    if (url.includes('/admin/add')) return 'Add Admin';
    if (url.includes('/admin')) return 'Admins';
    if (url.includes('/settings/attendance')) return 'Attendance Rules';
    if (url.includes('/settings/facility-profile')) return 'Facility Profile';
    if (url.includes('/settings/billing')) return 'Billing Settings';
    if (url.includes('/settings')) return 'Settings';
    if (url.includes('/auth/login')) return 'Facility portal · Sign in';
    if (url.includes('/auth/register')) return 'Facility portal · Sign up';
    if (url.includes('/auth/terms-of-service'))
      return 'HURO · Terms of Service';

    // Add here any additional URL patterns specific to the facility module if needed
    return 'HURO · Facility portal';
  }

  private setDocumentTitle(pageTitle: string): void {
    this.title.setTitle(`${pageTitle}`);
  }
}
